// src/components/instructor/ops/WeekOps.js
//
// Pass 49B §13 — weekly staff schedule: staff rows × 7 days, timed activities per cell, with
// conflict flags. Rows are selected-season operational staff. Derived from the shared source
// via buildWeekSchedule (operationsWorkspace) — no independent data model.

import React, { useMemo } from 'react';
import { buildWeekSchedule } from '../../../utils/operationsWorkspace';
import { zonedDateLabel, zonedTzAbbrev, LEGACY_OPS_TIMEZONE } from '../../../utils/opsTime';
import { resolveOrgTimezone } from '../../../utils/organizationRuntime';
import { isClientBooking } from '../../../utils/scheduleModel';
import { ActivityBadge, fmtTime } from './opsShared';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const isTimeoff = (b) => b.activityType === 'timeoff' || b.scheduleEntryType === 'blocked';

export default function WeekOps({ seasonBookings = [], seasonMembers = [], weekStart, organization = null, timeZone, conflicts = new Set(), onOpenBooking }) {
  // Pass 82C.2 §18 — the org's timezone drives the week grid buckets, the header date and the time labels; SAS
  // resolves Asia/Tokyo (identical), a non-JST tenant shows its own local week with no false "(JST)".
  const tz = timeZone || resolveOrgTimezone(organization) || LEGACY_OPS_TIMEZONE;
  const { days, rows, weekStart: start } = useMemo(
    () => buildWeekSchedule({ bookings: seasonBookings, members: seasonMembers, weekStart, conflicts, timeZone: tz }),
    [seasonBookings, seasonMembers, weekStart, conflicts, tz],
  );
  const cell = { padding: '6px 7px', borderBottom: '1px solid #ECEAE6', borderRight: '1px solid #F2F1EE', verticalAlign: 'top', minWidth: 92 };

  return (
    <div data-testid="week-ops" style={{ overflowX: 'auto' }}>
      <div style={{ fontSize: 12, color: '#6B6860', marginBottom: 8 }}>Week of {zonedDateLabel(start, tz)} ({zonedTzAbbrev(tz, start)})</div>
      <table style={{ borderCollapse: 'collapse', fontFamily: "'DM Sans',sans-serif", width: '100%' }}>
        <thead>
          <tr style={{ fontSize: 11, color: '#9E9B95', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            <th style={{ ...cell, textAlign: 'left', position: 'sticky', left: 0, background: '#fff', minWidth: 150 }}>Staff</th>
            {days.map((d, i) => <th key={d} style={{ ...cell, textAlign: 'left' }}>{DAY_LABELS[i]} <span style={{ color: '#C8C5BE' }}>{d.slice(8)}</span></th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.staffId} data-testid={`week-staff-${r.staffId}`}>
              <td style={{ ...cell, position: 'sticky', left: 0, background: '#fff', fontWeight: 600, fontSize: 12.5 }}>
                {r.staffName}{r.hasConflict ? <span style={{ color: '#DC2626', marginLeft: 4 }} title="Conflict this week">⚠</span> : null}
              </td>
              {days.map((d) => (
                <td key={d} style={cell}>
                  {(r.byDay[d] || []).map((b) => (isTimeoff(b) ? (
                    <div key={b.id} style={{ marginBottom: 3, display: 'flex', flexDirection: 'column', gap: 1, padding: '2px 4px', borderRadius: 6, background: '#F4F3F0', border: '1px dashed #C8C5BE' }} data-testid="week-rdo" title="Rostered day off — blocks capacity">
                      <span style={{ fontSize: 9, fontFamily: "'DM Mono',monospace", color: '#9E9B95' }}>All day</span>
                      <span style={{ fontSize: 9.5, fontWeight: 700, color: '#57534E' }}>RDO · Day Off</span>
                    </div>
                  ) : (onOpenBooking && isClientBooking(b) ? (
                    <button key={b.id} type="button" onClick={() => onOpenBooking(b)} style={{ marginBottom: 3, display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }} data-testid="week-entry">
                      <span style={{ fontSize: 9.5, fontFamily: "'DM Mono',monospace", color: conflicts.has(b.id) ? '#DC2626' : '#9E9B95' }}>{fmtTime(b.startAt, tz)}</span>
                      <ActivityBadge booking={b} />
                    </button>
                  ) : (
                    <div key={b.id} style={{ marginBottom: 3, display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="week-entry">
                      <span style={{ fontSize: 9.5, fontFamily: "'DM Mono',monospace", color: conflicts.has(b.id) ? '#DC2626' : '#9E9B95' }}>{fmtTime(b.startAt, tz)}</span>
                      <ActivityBadge booking={b} />
                    </div>
                  ))))}
                </td>
              ))}
            </tr>
          ))}
          {!rows.length ? <tr><td colSpan={8} style={{ ...cell, color: '#9E9B95' }}>No staff for this season.</td></tr> : null}
        </tbody>
      </table>
    </div>
  );
}
